import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { BRAND } from "@/lib/brand";

const STORAGE_KEY = "pax_disclaimer_ack_v1";

/**
 * First-visit modal that surfaces the mental-health disclaimer + crisis lines.
 * Non-blocking — the user acknowledges once and it's remembered locally.
 */
export default function DisclaimerGate() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
        } catch {
            // localStorage disabled — show once per session
            setOpen(true);
        }
    }, []);

    const ack = () => {
        try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch (e) { void e; }
        setOpen(false);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-[3px] p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="disclaimer-backdrop"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        transition={{ type: "spring", damping: 22, stiffness: 260 }}
                        className="bg-bone rounded-3xl border border-clayHover shadow-clay w-full max-w-lg p-7"
                        data-testid="disclaimer-modal"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <img src={BRAND.logo} alt="Pax" className="w-10 h-10 object-contain" />
                            <div>
                                <p className="text-xs uppercase tracking-[0.25em] text-inkSoft font-body">before we begin</p>
                                <h2 className="font-heading text-2xl tracking-tight text-ink">Pax is a companion, not a therapist.</h2>
                            </div>
                        </div>

                        <p className="text-sm text-inkSoft font-body leading-relaxed mb-5">
                            Pax is here to listen and reflect with you. She isn't a licensed clinician and can't
                            diagnose or treat. if you're in crisis or thinking about hurting yourself, please talk
                            to a real human first.
                        </p>

                        <div className="grid sm:grid-cols-2 gap-3 mb-6">
                            <a
                                href="tel:18005990019"
                                className="flex items-center gap-3 bg-white border border-clayHover text-ink rounded-2xl px-4 py-3 hover:bg-clay transition-colors font-body"
                                data-testid="disclaimer-kiran"
                            >
                                <Phone className="w-4 h-4 text-terracotta" />
                                <div>
                                    <div className="text-sm font-medium">India · KIRAN</div>
                                    <div className="text-xs text-inkSoft">1800-599-0019 · 24/7</div>
                                </div>
                            </a>
                            <a
                                href="tel:988"
                                className="flex items-center gap-3 bg-white border border-clayHover text-ink rounded-2xl px-4 py-3 hover:bg-clay transition-colors font-body"
                                data-testid="disclaimer-988"
                            >
                                <Phone className="w-4 h-4 text-terracotta" />
                                <div>
                                    <div className="text-sm font-medium">US · 988</div>
                                    <div className="text-xs text-inkSoft">suicide & crisis lifeline</div>
                                </div>
                            </a>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="text-xs text-inkSoft font-body">
                                by continuing, you agree to our{" "}
                                <Link to="/terms" className="text-terracotta hover:underline">terms</Link> &{" "}
                                <Link to="/privacy" className="text-terracotta hover:underline">privacy</Link>.
                            </div>
                            <button
                                onClick={ack}
                                data-testid="disclaimer-accept"
                                className="bg-terracotta text-bone px-6 py-3 rounded-full hover:bg-terracottaHover transition-colors font-body text-sm"
                            >
                                I understand — begin
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
